import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { HandHeart, Trash2 } from "lucide-react";
import { formatFechaHora } from "@/lib/api";
import { ReportButton } from "@/components/ReportButton";
import { useAuth } from "@/context/AuthContext";

export const PrayerCard = ({ prayer, onPray, onDelete }) => {
  const { user } = useAuth();
  const [pulse, setPulse] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const isLong = (prayer.texto || "").length > 180;
  const isOwner = !!(user && (prayer.is_owner || prayer.user_id === user.id));
  const canDelete = !!(onDelete && user && (isOwner || user.is_admin));
  const canReport = !!(user && !isOwner);

  const handlePray = () => {
    setPulse(true);
    setTimeout(() => setPulse(false), 500);
    onPray(prayer);
  };

  return (
    <Card className="border-border bg-card shadow-[var(--shadow-sm)]" data-testid={`prayer-card-${prayer.id}`}>
      <CardContent className="flex h-full flex-col gap-2.5 p-4">
        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
          <span className="font-semibold text-foreground">{prayer.user_name}</span>
          <span className="flex items-center gap-1.5">
            {formatFechaHora(prayer.created_at)}
            {canReport && <ReportButton targetType="prayer" targetId={prayer.id} />}
            {canDelete && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    type="button"
                    title="Eliminar petición"
                    aria-label="Eliminar petición"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground/70 transition-colors duration-150 hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    data-testid={`prayer-delete-${prayer.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-card">
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Eliminar esta petición?</AlertDialogTitle>
                    <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => onDelete(prayer)}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      data-testid={`prayer-confirm-delete-${prayer.id}`}
                    >
                      Eliminar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </span>
        </div>
        <p
          className={`whitespace-pre-line text-sm leading-relaxed text-foreground/90 ${expanded ? "" : "line-clamp-4"}`}
          data-testid="prayer-card-texto"
        >
          {prayer.texto}
        </p>
        {isLong && (
          <button
            onClick={() => setExpanded((e) => !e)}
            className="self-start text-xs font-semibold text-primary hover:text-primary/80"
            data-testid={`prayer-expand-${prayer.id}`}
          >
            {expanded ? "Ver menos" : "Ver más"}
          </button>
        )}
        <div className="mt-auto flex items-center justify-between gap-2 pt-1">
          <span className="text-xs text-muted-foreground" data-testid={`prayer-count-${prayer.id}`}>
            {prayer.pray_count === 1 ? "1 orando" : `${prayer.pray_count} orando`}
          </span>
          <Button
            variant={prayer.already_prayed ? "secondary" : "default"}
            size="sm"
            disabled={prayer.already_prayed}
            onClick={handlePray}
            className={`h-8 px-3 text-xs ${pulse ? "animate-gentle-pulse" : ""}`}
            data-testid={`prayer-pray-button-${prayer.id}`}
          >
            <HandHeart className="mr-1.5 h-3.5 w-3.5" />
            {prayer.already_prayed ? "Orando" : "Estoy orando por ti"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
